/**
 * Lenses scanner — reads a project's published prose files and projects them
 * into client-safe lens summaries.
 *
 * v1.1 structure: a "lens" is a folder under `<cwd>/_appostle-lenses/<slug>/` containing
 * a `prose.md` file. The folder name IS the slug. Files carrying a `lens:`
 * block in their YAML frontmatter are renderable: the block declares the
 * client-facing schema (title, subject spec, variable knobs). Prose files
 * without the block are surfaced as locked/coming-soon entries.
 *
 * IP boundary: the prose body is the agency's deliverable and must NEVER
 * leave this module — only the declared frontmatter schema is returned.
 * Operator-internal knobs (`lens.internal`) are likewise stripped.
 */

import { readdir, readFile } from "node:fs/promises";
import { join } from "path";
import { parse as parseYaml } from "yaml";
import {
  LensRenderSchemaSchema,
  LensSummarySchema,
  type LensSummary,
} from "../../shared/messages.js";
import { LENSES_RELATIVE_DIR, stripLensTypeSuffix } from "./paths.js";

/** Extracts the raw frontmatter block (between leading `---` fences), or null. */
function extractFrontmatter(source: string): string | null {
  if (!source.startsWith("---")) return null;
  const end = source.indexOf("\n---", 3);
  if (end === -1) return null;
  return source.slice(source.indexOf("\n") + 1, end);
}

function titleFromSlug(slug: string): string {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export async function scanLenses(cwd: string): Promise<LensSummary[]> {
  const dir = join(cwd, LENSES_RELATIVE_DIR);
  let entries: Array<{ name: string; isDirectory: () => boolean }>;
  try {
    entries = await readdir(dir, { withFileTypes: true });
  } catch {
    return [];
  }

  const lenses: LensSummary[] = [];
  const folders = entries
    .filter((e) => e.isDirectory() && !e.name.startsWith("."))
    .map((e) => e.name)
    .sort();
  for (const folder of folders) {
    // Folder names may carry a Finder-facing " [type]" suffix; the wire slug never does.
    const slug = stripLensTypeSuffix(folder);
    let raw: string;
    try {
      raw = await readFile(join(dir, folder, "prose.md"), "utf8");
    } catch {
      // A lens folder without prose.md is skipped silently.
      continue;
    }

    let schema: LensSummary["schema"] = null;
    const frontmatter = extractFrontmatter(raw);
    if (frontmatter) {
      try {
        const data = parseYaml(frontmatter) as { lens?: unknown } | null;
        if (data && typeof data === "object" && data.lens) {
          const parsed = LensRenderSchemaSchema.safeParse(data.lens);
          if (parsed.success) {
            schema = parsed.data;
          }
        }
      } catch {
        // Malformed frontmatter → treat as schemaless (coming soon).
      }
    }

    const parsedType = LensSummarySchema.shape.type.safeParse(schema?.type);
    const type: LensSummary["type"] = parsedType.success
      ? parsedType.data
      : slug.endsWith("-composite")
        ? "composite"
        : "generate";

    lenses.push({
      slug,
      title: schema?.title ?? titleFromSlug(slug),
      description: schema?.description ?? null,
      type,
      schema,
    });
  }
  return lenses;
}
